"use client";

import Link from "next/link";
import { useEffect } from "react";
import dynamicImport from "next/dynamic";

const AdvertiseIconSVG = dynamicImport(
  () =>
    import("@/public/svgs/advertise-icon").then((mod) => ({
      default: mod.AdvertiseIconSVG,
    })),
  {
    loading: () => (
      <div className="h-5 w-5 animate-pulse bg-surface-hover rounded" />
    ),
  }
);

// Extend Window interface for qrAdvanced
declare global {
  interface Window {
    qrAdvanced?: {
      key: string;
      q: Array<{ e: string; m?: Record<string, unknown>; t: number }>;
      track: (event: string, metadata?: Record<string, unknown>) => void;
      _send: (event: string, metadata?: Record<string, unknown>) => void;
      ready?: number;
    };
  }
}

export default function AdvertiseButton() {
  useEffect(() => {
    // QR Advanced Conversion Tracking initialization
    (function (w: Window, d: Document, k: string, u: string) {
      w.qrAdvanced = w.qrAdvanced || ({} as NonNullable<Window["qrAdvanced"]>);
      w.qrAdvanced.key = k;
      w.qrAdvanced.q = [];
      w.qrAdvanced.track = function (e: string, m?: Record<string, unknown>) {
        this.q.push({ e: e, m: m, t: Date.now() });
        if (this.ready) this._send(e, m);
      };
      w.qrAdvanced._send = function (e: string, m?: Record<string, unknown>) {
        const ls = localStorage;
        const ss = sessionStorage;
        const p = new URLSearchParams(location.search);
        // Get QR ID (from URL param 'qr' or localStorage)
        let qr = p.get("qr") || ls.getItem("qr_id");
        if (qr) {
          ls.setItem("qr_id", qr);
          ls.setItem("qr_ts", Date.now().toString());
        }
        // Get scan session ID (from URL param 'qr_scan' or localStorage)
        // This links events to the specific QR scan for deduplication
        let scan = p.get("qr_scan") || ls.getItem("qr_scan");
        if (scan) ls.setItem("qr_scan", scan);
        // Browser session ID (resets when tab closes)
        let sid =
          ss.getItem("qr_session") ||
          "s_" + Math.random().toString(36).substr(2, 9);
        ss.setItem("qr_session", sid);
        fetch(u + "/api/conversions/track", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            trackingKey: k,
            eventType: e === "custom" ? "custom" : e,
            eventName: e,
            qrId: qr,
            scanId: scan,
            sessionId: sid,
            metadata: m,
          }),
        }).catch(function () {});
      };
      w.qrAdvanced.ready = 1;
      w.qrAdvanced.q.forEach(function (i) {
        w.qrAdvanced!._send(i.e, i.m);
      });
    })(
      window,
      document,
      "tk_PTer5BmgO8RmbCIu-ZP1EfMCLEvNubAZ",
      "https://api.qradvanced.com"
    );
  }, []);

  const handleClick = () => {
    if (typeof window !== "undefined" && window.qrAdvanced) {
      const ls = localStorage;
      const ss = sessionStorage;
      const p = new URLSearchParams(location.search);

      // Get QR tracking values
      const qr_id = p.get("qr") || ls.getItem("qr_id") || undefined;
      const qr_scan = p.get("qr_scan") || ls.getItem("qr_scan") || undefined;
      const qr_session = ss.getItem("qr_session") || undefined;

      window.qrAdvanced.track("button_click", {
        button: "advertise_cta",
        qr_id,
        qr_scan,
        qr_session,
      });
    }
  };

  return (
    <Link
      href="/about-techno-music-world#contact"
      className="flex items-center gap-x-2 justify-center w-full text-primary-foreground uppercase font-bold rounded-md tracking-wide h-9 bg-primary px-4 py-2 hover:bg-primary/90 transition-colors text-sm"
      onClick={handleClick}
    >
      <AdvertiseIconSVG className="h-5 w-5 mb-0.5" />
      <span>Advertise on our platform</span>
    </Link>
  );
}
